import React, { useState, useEffect } from 'react';
import axios from '../services/axiosConfig';
import { exportPagos } from '../utils/exportUtils';
import ConfirmDialog from './ConfirmDialog';
import config from '../config';
import '../styles/pagos.css';

function Pagos() {
  const [pagos, setPagos] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [tarifas, setTarifas] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [pagoEditando, setPagoEditando] = useState(null);
  const [busquedaPaciente, setBusquedaPaciente] = useState('');
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);
  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [pagoAEliminar, setPagoAEliminar] = useState(null);
  const [mostrarConfirmEliminar, setMostrarConfirmEliminar] = useState(false);
  const [formData, setFormData] = useState({
    paciente_id: '',
    monto: '',
    fecha_pago: new Date().toISOString().split('T')[0],
    metodo_pago: 'efectivo',
    tipo_sesion: '45_minutes',
    estado: 'pagado'
  });

  const cargarDatos = async () => {
    try {
      const [pagosRes, pacientesRes, tarifasRes] = await Promise.all([
        axios.get('/pagos'),
        axios.get('/pacientes'),
        axios.get('/tarifas')
      ]);
      // Manejar respuestas paginadas o arrays directos
      const pagosData = pagosRes.data;
      const pacientesData = pacientesRes.data;
      setPagos(Array.isArray(pagosData) ? pagosData : (pagosData?.data || []));
      setPacientes(Array.isArray(pacientesData) ? pacientesData : (pacientesData?.data || []));
      setTarifas(tarifasRes.data || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
      setPagos([]);
      setPacientes([]);
      setTarifas([]);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modoEdicion) {
        await axios.put(`/pagos/${pagoEditando.id}`, formData);
      } else {
        await axios.post('/pagos', formData);
      }
      setMostrarForm(false);
      setModoEdicion(false);
      setPagoEditando(null);
      setPacienteSeleccionado(null);
      setBusquedaPaciente('');
      setFormData({
        paciente_id: '',
        monto: '',
        fecha_pago: new Date().toISOString().split('T')[0],
        metodo_pago: 'efectivo',
        tipo_sesion: '45_minutes',
        estado: 'pagado'
      });
      cargarDatos();
    } catch (error) {
      console.error('Error guardando pago:', error);
      alert('Error al guardar el pago');
    }
  };

  const seleccionarPaciente = (paciente) => {
    setPacienteSeleccionado(paciente);
    setBusquedaPaciente(paciente.nombre);
    setFormData({...formData, paciente_id: paciente.id});
    setMostrarResultados(false);
  };

  const pacientesFiltrados = pacientes.filter(p => 
    busquedaPaciente && (
      p.nombre?.toLowerCase().includes(busquedaPaciente.toLowerCase()) ||
      p.email?.toLowerCase().includes(busquedaPaciente.toLowerCase()) ||
      p.telefono?.includes(busquedaPaciente)
    )
  );

  const handleTipoSesionChange = (tipo) => {
    const tarifa = tarifas.find(t => t.tipo_sesion === tipo);
    setFormData({
      ...formData, 
      tipo_sesion: tipo,
      monto: tarifa ? tarifa.precio : formData.monto
    });
  };

  const abrirFormulario = () => {
    setMostrarForm(true);
    setModoEdicion(false);
    setPagoEditando(null);
    setPacienteSeleccionado(null);
    setBusquedaPaciente('');
    setFormData({
      paciente_id: '',
      monto: '',
      fecha_pago: new Date().toISOString().split('T')[0],
      metodo_pago: 'efectivo',
      tipo_sesion: '45_minutes',
      estado: 'pagado'
    });
  };

  const cerrarFormulario = () => {
    setMostrarForm(false);
    setModoEdicion(false);
    setPagoEditando(null);
    setPacienteSeleccionado(null);
    setBusquedaPaciente('');
    setMostrarResultados(false);
  };

  const editarPago = (pago) => {
    setModoEdicion(true);
    setPagoEditando(pago);
    setMostrarForm(true);
    
    // Encontrar el paciente
    const paciente = pacientes.find(p => p.id === pago.paciente_id);
    if (paciente) {
      setPacienteSeleccionado(paciente);
      setBusquedaPaciente(paciente.nombre);
    }
    
    setFormData({
      paciente_id: pago.paciente_id,
      turno_id: pago.turno_id || null,
      monto: pago.monto,
      fecha_pago: pago.fecha_pago,
      metodo_pago: pago.metodo_pago,
      tipo_sesion: pago.tipo_sesion || '45_minutes',
      estado: pago.estado
    });
  };

  const eliminarPago = (pago) => {
    setPagoAEliminar(pago);
    setMostrarConfirmEliminar(true);
  };

  const confirmarEliminar = async () => {
    try {
      await axios.delete(`/pagos/${pagoAEliminar.id}`);
      cargarDatos();
      setMostrarConfirmEliminar(false);
      setPagoAEliminar(null);
    } catch (error) {
      console.error('Error eliminando pago:', error);
      alert('Error al eliminar el pago');
    }
  };

  const cancelarEliminar = () => {
    setMostrarConfirmEliminar(false);
    setPagoAEliminar(null);
  };

  return (
    <div className="pagos-page">
      <div className="page-header">
        <h2>Gestión de Pagos</h2>
        <div className="header-actions">
          <button 
            className="btn-secondary"
            onClick={() => exportPagos(pagos, 'excel')}
            title="Exportar a Excel"
          >
            📊 Excel
          </button>
          <button 
            className="btn-secondary"
            onClick={() => exportPagos(pagos, 'pdf')}
            title="Exportar a PDF"
          >
            📄 PDF
          </button>
          <button 
            className="btn-primary"
            onClick={abrirFormulario}
          >
            💰 Registrar Pago
          </button>
        </div>
      </div>

      {mostrarForm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>{modoEdicion ? 'Editar Pago' : 'Registrar Pago'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="busqueda-paciente-container">
                <label>Buscar Paciente</label>
                <div className="input-con-resultados">
                  <input
                    type="text"
                    placeholder="🔍 Buscar por nombre, email o teléfono..."
                    value={busquedaPaciente}
                    onChange={(e) => {
                      setBusquedaPaciente(e.target.value);
                      setMostrarResultados(true);
                      if (!e.target.value) {
                        setPacienteSeleccionado(null);
                        setFormData({...formData, paciente_id: ''});
                      }
                    }}
                    onFocus={() => busquedaPaciente && setMostrarResultados(true)}
                    required
                  />
                  
                  {mostrarResultados && busquedaPaciente && (
                    <div className="resultados-busqueda">
                      {pacientesFiltrados.length > 0 ? (
                        pacientesFiltrados.slice(0, 5).map(paciente => (
                          <div 
                            key={paciente.id}
                            className="resultado-item"
                            onClick={() => seleccionarPaciente(paciente)}
                          >
                            <div className="resultado-nombre">{paciente.nombre}</div>
                            <div className="resultado-info">
                              {paciente.email} • {paciente.telefono}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="no-resultados">
                          No se encontraron pacientes
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {pacienteSeleccionado && (
                  <div className="paciente-seleccionado">
                    ✅ Paciente: <strong>{pacienteSeleccionado.nombre}</strong>
                    <button
                      type="button"
                      className="btn-limpiar"
                      onClick={() => {
                        setPacienteSeleccionado(null);
                        setBusquedaPaciente('');
                        setFormData({...formData, paciente_id: ''});
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
              
              <label>Tipo de Sesión</label>
              <select
                value={formData.tipo_sesion}
                onChange={(e) => handleTipoSesionChange(e.target.value)}
              >
                <option value="30_minutes">
                  {config.sessionLabels['30_minutes']} 
                  {tarifas.find(t => t.tipo_sesion === '30_minutes') && 
                    ` - $${tarifas.find(t => t.tipo_sesion === '30_minutes').precio}`
                  }
                </option>
                <option value="45_minutes">
                  {config.sessionLabels['45_minutes']}
                  {tarifas.find(t => t.tipo_sesion === '45_minutes') && 
                    ` - $${tarifas.find(t => t.tipo_sesion === '45_minutes').precio}`
                  }
                </option>
                <option value="pack_4_sessions">
                  {config.sessionLabels['pack_4_sessions']}
                  {tarifas.find(t => t.tipo_sesion === 'pack_4_sessions') && 
                    ` - $${tarifas.find(t => t.tipo_sesion === 'pack_4_sessions').precio}`
                  }
                </option>
              </select>
              
              <label>Monto</label>
              <input
                type="number"
                placeholder="Monto"
                value={formData.monto}
                onChange={(e) => setFormData({...formData, monto: e.target.value})}
                required
                step="0.01"
              />
              <input
                type="date"
                value={formData.fecha_pago}
                onChange={(e) => setFormData({...formData, fecha_pago: e.target.value})}
                required
              />
              <select
                value={formData.metodo_pago}
                onChange={(e) => setFormData({...formData, metodo_pago: e.target.value})}
              >
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">Transferencia</option>
                <option value="tarjeta">Tarjeta</option>
              </select>
              <select
                value={formData.estado}
                onChange={(e) => setFormData({...formData, estado: e.target.value})}
              >
                <option value="pagado">Pagado</option>
                <option value="pendiente">Pendiente</option>
              </select>
              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  {modoEdicion ? 'Guardar Cambios' : 'Registrar'}
                </button>
                <button 
                  type="button" 
                  className="btn-secondary"
                  onClick={cerrarFormulario}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="pagos-list">
        {pagos.map(pago => (
          <div key={pago.id} className="pago-card">
            <div className="pago-info">
              <h3>{pago.paciente_nombre}</h3>
              <p>💵 Monto: ${pago.monto}</p>
              <p>📅 Fecha: {new Date(pago.fecha_pago).toLocaleDateString()}</p>
              <p>💳 Método: {pago.metodo_pago}</p>
              {pago.tipo_sesion && (
                <p>🎯 Tipo: {config.sessionLabels[pago.tipo_sesion] || pago.tipo_sesion}</p>
              )}
              <span className={`estado ${pago.estado}`}>{pago.estado}</span>
            </div>
            <div className="pago-acciones">
              <button 
                className="btn-editar"
                onClick={() => editarPago(pago)}
                title="Editar pago"
              >
                ✏️
              </button>
              <button 
                className="btn-eliminar"
                onClick={() => eliminarPago(pago)}
                title="Eliminar pago"
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de Confirmación de Eliminación */}
      <ConfirmDialog
        isOpen={mostrarConfirmEliminar}
        title="Eliminar Pago"
        message={`¿Deseas eliminar el pago de $${pagoAEliminar?.monto || 0}?`}
        detail={`Paciente: ${pagoAEliminar?.paciente_nombre || 'N/A'} | Fecha: ${pagoAEliminar ? new Date(pagoAEliminar.fecha_pago).toLocaleDateString() : ''}`}
        confirmText="Sí, eliminar"
        cancelText="Cancelar"
        onConfirm={confirmarEliminar}
        onCancel={cancelarEliminar}
        isDangerous={true}
      />
    </div>
  );
}

export default Pagos;
